import React, { useState } from 'react';
import { AlertTriangle, ChevronRight, Layers } from 'lucide-react';
import AceiteBoletoUnicoModal from './AceiteBoletoUnicoModal';
import { useConviteBoletoUnico } from '../../src/hooks/useConviteBoletoUnico';
import { enderecoCurtoUc, ROTULOS_SITUACAO_PORTAL } from '../../src/lib/boletoUnico/aceitePortal';

// Cartão do boleto único no Início do Portal (tarefa #5575). É o lugar fixo do convite: o modal
// abre sozinho só na primeira visita, e daqui em diante o cliente decide quando quiser, sem ser
// interrompido. Também responde "eu já aderi?" para quem aceitou ou já está no boleto único.
//
// Não aparece para quem não tem nada a ver com o convite (só UC de parceira ou inativa). Sem
// aviso publicado, mostra só a situação de quem já aceitou — sem botão, porque não há texto
// oficial vigente para aceitar.

const TOM: Record<string, string> = {
  pode_aderir: 'text-secondary-dark bg-secondary/15',
  aceito_aguardando: 'text-info bg-info/10',
  ativo: 'text-primary bg-primary/10',
};

const CartaoBoletoUnico = () => {
  const { carregando, aviso, convite, aceitar } = useConviteBoletoUnico();
  const [aberto, setAberto] = useState(false);

  if (carregando || !convite || !convite.mostrarConvite) return null;

  const podeAceitarAgora = !!aviso && convite.podeAderir > 0;
  // Sem convite publicado e sem nada aceito, não há o que mostrar.
  if (!podeAceitarAgora && convite.aguardando === 0 && convite.ativas === 0) return null;

  const linhasVisiveis = podeAceitarAgora ? convite.linhas : convite.linhas.filter(l => l.situacao !== 'pode_aderir');

  return (
    <section aria-labelledby="boleto-unico-titulo">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-secondary/10 flex items-center justify-center shrink-0">
            <Layers size={22} className="text-secondary" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="boleto-unico-titulo" className="text-lg font-bold text-gray-900">Boleto único</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {podeAceitarAgora
                ? 'A partir de outubro de 2026, a fatura da Equatorial e o boleto da Vigor num pagamento só. É opcional.'
                : 'Situação da adesão das suas unidades.'}
            </p>
          </div>
        </div>

        <ul className="mt-4 divide-y divide-gray-100">
          {linhasVisiveis.map(({ uc, situacao, debitoAutomatico }) => {
            const rotulo = ROTULOS_SITUACAO_PORTAL[situacao as keyof typeof ROTULOS_SITUACAO_PORTAL];
            const endereco = enderecoCurtoUc(uc);
            return (
              <li key={uc.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-semibold text-gray-900">UC {uc.numero_uc}</span>
                  <span className={`text-xs font-bold rounded-full px-2.5 py-0.5 ${TOM[situacao]}`}>{rotulo.texto}</span>
                </div>
                {endereco && <p className="text-xs text-gray-500 mt-0.5">{endereco}</p>}
                <p className="text-xs text-gray-600 mt-1">{rotulo.ajuda}</p>
                {debitoAutomatico && situacao !== 'ativo' && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-amber-800">
                    <AlertTriangle size={13} className="shrink-0 mt-px" aria-hidden="true" />
                    A última fatura mostra débito automático na Equatorial. Cancele no banco antes de aderir.
                  </p>
                )}
              </li>
            );
          })}
        </ul>

        {podeAceitarAgora && (
          <button
            onClick={() => setAberto(true)}
            className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-all"
          >
            Ver condições e aceitar
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        )}
      </div>

      {aberto && aviso && (
        <AceiteBoletoUnicoModal
          aviso={aviso}
          linhas={convite.linhas}
          onAceitar={aceitar}
          onFechar={() => setAberto(false)}
        />
      )}
    </section>
  );
};

export default CartaoBoletoUnico;
